import { Avatar, styled } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CellPropsBase, CellTitle, CellValueArea, FormCell } from '.'
import { ReferencedEntityWithIcon } from '../../../../../domain/value-object/ReferencedEntity'
import { ProjectMemberSearchOption } from '../../../../../domain/value-object/ProjectMemberSearchOption'
import { useProjectMemberRepository } from '../../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { colorPalette } from '../../../../style/colorPalette'
import { useDebounce } from '../../../../hooks/useDebounce'
import {
  ProjectMemberPopper,
  ProjectMemberPopperHandle,
} from '../../../../features/ProjectMemberPopper'
import { ProjectMemberInput } from '../../../../features/ProjectMemberInput'

type ProjectMemberAutocompleteCellProps = {
  value: ReferencedEntityWithIcon | undefined
  onChange?: (v: ReferencedEntityWithIcon | undefined) => void
} & CellPropsBase

export const ProjectMemberAutocompleteCell = ({
  cellWidth,
  label,
  value,
  onChange,
  editable = true,
  required = false,
  edited,
  validationErrors,
}: ProjectMemberAutocompleteCellProps) => {
  const disabled = !editable
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<ProjectMemberSearchOption[]>([])
  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebounce(searchText, 300)
  const { searchAsProjectMemberSearchOption } = useProjectMemberRepository()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popperRef = useRef<ProjectMemberPopperHandle>(null)

  useEffect(() => {
    if (!isOpen) return
    let isMounted = true
    const fn = async () => {
      const response =
        await searchAsProjectMemberSearchOption(debouncedSearchText)
      if (isMounted) {
        setOptions(response)
      }
    }
    fn()
    return () => {
      isMounted = false
    }
  }, [searchAsProjectMemberSearchOption, debouncedSearchText, isOpen])

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value)
      if (!isOpen) {
        setIsOpen(true)
      }
    },
    [isOpen]
  )

  const handleFocus = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    setSearchText('')
  }, [disabled])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSearchText('')
  }, [])

  const handleSelect = useCallback(
    (member: ProjectMemberSearchOption) => {
      onChange?.({
        uuid: member.uuid,
        name: member.name,
        iconUrl: member.iconUrl ?? undefined,
      })
      handleClose()
    },
    [onChange, handleClose]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowUp()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        popperRef.current?.enter()
      } else if (event.key === 'Escape') {
        handleClose()
        inputRef.current?.blur()
      }
    },
    [handleClose]
  )

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onChange?.(undefined)
      inputRef.current?.focus()
    },
    [onChange]
  )

  const displayValue = isOpen ? searchText : (value?.name ?? '')

  return (
    <FormCell cellWidth={cellWidth}>
      <CellTitle title={label} required={required} />
      <CellValueArea edited={edited} validationErros={validationErrors}>
        <Container ref={containerRef}>
          <InputArea>
            {value && !isOpen && (
              <MemberAvatar src={value.iconUrl ?? undefined} />
            )}
            <ProjectMemberInput
              inputRef={inputRef}
              value={displayValue}
              onChange={handleSearchChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              autoFocus={false}
            />
            {value && !disabled && (
              <ClearButton onClick={handleClear}>×</ClearButton>
            )}
          </InputArea>
          {isOpen && (
            <>
              <ProjectMemberPopper
                ref={popperRef}
                open={isOpen}
                anchorEl={containerRef.current}
                options={options}
                onSelect={handleSelect}
                inputRef={inputRef}
                searchText={searchText}
              />
              <Backdrop onClick={handleClose} />
            </>
          )}
        </Container>
      </CellValueArea>
    </FormCell>
  )
}

const Container = styled('div')({
  width: '100%',
  position: 'relative',
})

const InputArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: '24px',
})

const ClearButton = styled('button')({
  marginLeft: 'auto',
  padding: '0 4px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: colorPalette.monotone[4],
  fontSize: '14px',
  '&:hover': {
    color: colorPalette.monotone[6],
  },
})

const Backdrop = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1299,
})

const MemberAvatar = styled(Avatar)({
  width: '20px',
  height: '20px',
  marginRight: '5px',
})
